import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import type {
  AuditEvent,
  AuditTrailEntry,
  LoginActivity,
  PdSession,
} from 'src/app/core/services/admin-session-api.service';
import { formatSessionRuntime } from 'src/app/shared/utils/session-runtime';
import {
  AuditEntryType,
  loginActivityRuntimeDisplay,
  loginActivityStatusClass,
  loginActivityStatusLabel,
  loginActivityUserEmail,
  loginActivityUserName,
} from '../audit-trail-display.util';
import { isSuperAdminSimulatePdSession } from 'src/app/shared/utils/pd-session-list.util';

@Component({
  selector: 'app-audit-trail-table',
  standalone: true,
  imports: [CommonModule, IconModule, HasPermissionPipe, ListModuleTableComponent, ListCellDefDirective],
  templateUrl: './audit-trail-table.component.html',
})
export class AuditTrailTableComponent {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  @Input() rows: AuditTrailEntry[] = [];
  /** Which tab is active — a sign-in has no agent, customer, event or score. */
  @Input() entryType: AuditEntryType = '';
  @Input() loading = false;
  @Input() emptyMessage =
    'No records found. Run a simulation or sign in to the portal to see activity here.';

  @Output() viewSession = new EventEmitter<PdSession>();
  @Output() viewLogin = new EventEmitter<LoginActivity>();
  @Output() viewEvent = new EventEmitter<AuditEvent>();

  get columns(): ListModuleColumnDef[] {
    // On the sign-ins tab the type and event columns are constant, and score never
    // applies — so they are dropped and the shared cells relabelled to what a
    // sign-in row actually holds.
    if (this.entryType === 'login') {
      return [
        { id: 'date', header: 'Date' },
        { id: 'agent', header: 'User', cellClass: 'min-w-[10rem] max-w-[16rem] truncate text-sm' },
        { id: 'customer', header: 'Email', cellClass: 'max-w-[14rem] truncate text-sm' },
        { id: 'result', header: 'Status', align: 'center', cellClass: 'whitespace-nowrap text-center' },
        { id: 'runtime', header: 'Device', cellClass: 'whitespace-nowrap text-xs text-gray-500' },
        { id: 'actions', header: 'Action', align: 'right', cellClass: 'text-end' },
      ];
    }
    return [
      { id: 'date', header: 'Date' },
      { id: 'type', header: 'Type', cellClass: 'whitespace-nowrap' },
      { id: 'agent', header: 'Agent', cellClass: 'min-w-[10rem] max-w-[16rem] truncate text-sm' },
      { id: 'customer', header: 'Customer', cellClass: 'max-w-[10rem] truncate text-sm' },
      { id: 'event', header: 'Event', cellClass: 'whitespace-nowrap text-sm' },
      { id: 'result', header: 'Result', align: 'center', cellClass: 'whitespace-nowrap text-center' },
      { id: 'score', header: 'Score', align: 'center', cellClass: 'whitespace-nowrap text-center' },
      { id: 'runtime', header: 'Runtime', cellClass: 'whitespace-nowrap text-xs text-gray-500' },
      { id: 'actions', header: 'Action', align: 'right', cellClass: 'text-end' },
    ];
  }

  isSession(row: AuditTrailEntry): row is { kind: 'session'; sort_at: string; session: PdSession } {
    return row.kind === 'session';
  }

  canOpenRowDetail(row: AuditTrailEntry): boolean {
    if (!this.isSession(row)) return true;
    return isSuperAdminSimulatePdSession(row.session);
  }

  /** One handler for three row kinds, so the template stops nesting ternaries per column. */
  openDetail(row: AuditTrailEntry): void {
    if (this.isSession(row)) {
      if (!isSuperAdminSimulatePdSession(row.session)) return;
      return this.viewSession.emit(row.session);
    }
    if (this.isLogin(row)) return this.viewLogin.emit(row.login);
    this.viewEvent.emit(row.event);
  }

  isEvent(row: AuditTrailEntry): row is { kind: 'event'; sort_at: string; event: AuditEvent } {
    return row.kind === 'event';
  }

  /** 'pd_link #19' — the record the action touched, or a dash when it named none. */
  eventResource(event: AuditEvent): string {
    if (!event.resource_type) return '—';
    return event.resource_id ? `${event.resource_type} #${event.resource_id}` : event.resource_type;
  }

  /** Who acted. Anything the tenant service recorded has no operator behind it, only a source. */
  eventActor(event: AuditEvent): string {
    return event.actor_email ?? event.source ?? '—';
  }

  eventOutcomeLabel(event: AuditEvent): string {
    if (event.outcome === 'success') return 'Success';
    if (event.outcome === 'failure') return 'Failed';
    return '—';
  }

  eventOutcomeClass(event: AuditEvent): string {
    return event.outcome === 'failure'
      ? 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200'
      : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200';
  }

  isLogin(row: AuditTrailEntry): row is { kind: 'login'; sort_at: string; login: LoginActivity } {
    return row.kind === 'login';
  }

  entryDateDisplay(iso: string): string {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  entryTimeDisplay(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  sessionType(s: PdSession): string {
    const src = String(s.metadata?.['source'] ?? '');
    if (src === 'kyc_simulate' || src === 'simulate') return 'KYC Bot';
    if (s.ai_response?.pan || s.ai_response?.face) return 'KYC Bot';
    return 'PD Bot';
  }

  sessionTypeClass(s: PdSession): string {
    return this.sessionType(s) === 'KYC Bot'
      ? 'bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300'
      : 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300';
  }

  agentName(s: PdSession): string {
    return s.agent?.name ?? `Agent #${s.agent_id}`;
  }

  eventLabel(s: PdSession): string {
    if (s.ai_response?.pan || s.ai_response?.face) return 'KYC Verification';
    const src = String(s.metadata?.['source'] ?? '');
    if (src === 'kyc_simulate' || src === 'simulate') return 'KYC Verification';
    return s.status ?? 'Session';
  }

  sessionResult(s: PdSession): string | null {
    const col = (s.result ?? '').trim().toUpperCase();
    if (col) return col;
    const fromAi = (s.ai_response?.result ?? '').trim().toUpperCase();
    if (fromAi === 'PASS' || fromAi === 'REVIEW' || fromAi === 'FAIL') return fromAi;
    const score = s.ai_response?.overall_score ?? s.ai_response?.composite_score ?? s.score;
    if (score != null) {
      const n = Number(score);
      if (!Number.isNaN(n)) {
        if (n >= 70) return 'PASS';
        if (n >= 40) return 'REVIEW';
        return 'FAIL';
      }
    }
    return null;
  }

  statusLabel(s: PdSession): string {
    const r = this.sessionResult(s);
    if (r === 'PASS') return 'PASS';
    if (r === 'REVIEW') return 'REVIEW';
    if (r === 'FAIL') return 'FAIL';
    return 'PENDING';
  }

  statusClass(s: PdSession): string {
    const r = this.statusLabel(s);
    if (r === 'PASS') return 'text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-950/50';
    if (r === 'REVIEW') return 'text-amber-700 bg-amber-100 dark:text-amber-200 dark:bg-amber-950/50';
    if (r === 'FAIL') return 'text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-950/50';
    return 'text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-white/5';
  }

  scoreDisplay(s: PdSession): string {
    const score = s.ai_response?.overall_score ?? s.ai_response?.composite_score ?? s.score;
    if (score == null) return '—';
    return `${Math.round(Number(score))}`;
  }

  scoreClass(s: PdSession): string {
    const r = this.sessionResult(s);
    if (r === 'PASS') return 'text-emerald-600 dark:text-emerald-400';
    if (r === 'FAIL') return 'text-red-600 dark:text-red-400';
    if (r === 'REVIEW') return 'text-amber-600 dark:text-amber-400';
    return 'text-gray-700 dark:text-gray-300';
  }

  readonly loginUserName = loginActivityUserName;
  readonly loginUserEmail = loginActivityUserEmail;
  readonly loginStatusLabel = loginActivityStatusLabel;
  readonly loginStatusClass = loginActivityStatusClass;
  readonly loginRuntimeDisplay = loginActivityRuntimeDisplay;

  runtimeDisplay(s: PdSession): string {
    return formatSessionRuntime(s);
  }
}
